import { MD5 } from "crypto-js";
import { ResponseModelInterfaceStatic } from "@/service/models/response-model-interface";
import { XmlDecodingException } from "@/utilities/exceptions/xml-decoding-exception";
import { staticImplements } from "@/utilities/static-implements";
import { XmlElementWrapper } from "@/utilities/xml-helpers/xml-element-wrapper";
import { DrugActiveMoiety } from "./drug-active-moiety";
import { DrugCovariate } from "./covariate/drug-covariate";
import { FormulationAndRouteFull } from "./formulation-and-route-full";

@staticImplements<ResponseModelInterfaceStatic>()
export class DrugModelLight
{
    public constructor(
        public id: string,
        public modelId: string,
        public domainDescription: string,
        public covariates: DrugCovariate[],
        public activeMoieties: DrugActiveMoiety[],
        public formulationAndRoutes: Map<string, FormulationAndRouteFull>,
        public comments: string[],
        protected defaultFormulationAndRouteId: string,
    )
    {
    }

    public static makeFromXml(elem: XmlElementWrapper): DrugModelLight
    {
        const id = elem.firstChildByTag("drugId").value();
        const modelId = elem.firstChildByTag("drugModelId").value();
        const formulationAndRoutes = new Map<string, FormulationAndRouteFull>();

        const domainDescription = elem
            .firstChildByTag("domain")
            .firstChildByTag("description")
            .translatedElemValue("desc");

        const comments = elem.firstChildByTag("comments").translatedElemValues("comment");

        // Handle covariates
        const covariates = elem
            .firstChildByTag("covariates")
            .mapForEachChildTag("covariate", (subElem) => DrugCovariate.makeFromXml(subElem));

        // Handle active moieties
        const activeMoieties = elem
            .firstChildByTag("activeMoieties")
            .mapForEachChildTag("activeMoiety", (subElem) => DrugActiveMoiety.makeFromXml(subElem));

        // Handle formulations and routes
        const formulationAndRoutesElem = elem.firstChildByTag("formulationAndRoutes");
        const defaultFormulationAndRouteId = formulationAndRoutesElem.attrValue("default");

        formulationAndRoutesElem.forEachChildWithTag("formulationAndRoute", (subElem: XmlElementWrapper) => {
            const formulationAndRoute = FormulationAndRouteFull.makeFromXml(subElem);
            formulationAndRoutes.set(formulationAndRoute.id, formulationAndRoute);
        });

        return new this(
            id,
            modelId,
            domainDescription,
            covariates,
            activeMoieties,
            formulationAndRoutes,
            comments,
            defaultFormulationAndRouteId
        );
    }

    // Having a domain hash is convenient to avoid comparing between
    // full domain descriptions. In a model like this one we can achieve
    // it using a getter.
    public get domainHash(): string
    {
        return MD5(this.domainDescription).toString()
    }

    public get defaultFormulationAndRoute(): FormulationAndRouteFull
    {
        const defaultFormulationAndRoute = this.formulationAndRoutes.get(this.defaultFormulationAndRouteId);

        if (defaultFormulationAndRoute === undefined) {
            throw new XmlDecodingException(`No <formulationAndRoute> was found for default id "${this.defaultFormulationAndRouteId}"`);
        }

        return defaultFormulationAndRoute;
    }

    public getFormulationAndRoute(id: string): FormulationAndRouteFull|null
    {
        return this.formulationAndRoutes.get(id) ?? null;
    }

    public getDrugCovariateById(id: string): DrugCovariate|null
    {
        for (const covariate of this.covariates) {
            if (covariate.id === id) {
                return covariate;
            }
        }

        return null;
    }
}
